/**
 * Client schema-version stamp for the auth + onboarding surface.
 *
 * The browser keeps two pieces of long-lived state that the server cannot
 * see and that survive across deploys:
 *
 *   1. httpOnly auth cookies (`accessToken`, `refreshToken`, `_csrf`)
 *      signed by the current JWT secret. If the secret rotates or the
 *      token shape changes, the cookies look "valid" to `proxy.ts`'s
 *      cheap presence check but fail every `/auth/me` lookup.
 *   2. `localStorage` markers (`loci.session.present`, the session hint;
 *      `loci:onboardingCompleted`, the anonymous / pre-FRE-801 fallback
 *      flag). Their *semantics* can shift even when the keys do not —
 *      FRE-801 (WS-2 M1) is the canonical example: the gate stopped
 *      trusting the local marker for authed users, so every existing
 *      user broke until backfill landed.
 *
 * The existing recovery path only fires after the user reaches a route that
 * triggers `/auth/me` and gets a 401, leaving a long window where the UI
 * looks stuck.
 *
 * This module records "the client booted at version N" in localStorage.
 * When code that changes the meaning of stored auth/onboarding state
 * ships, bump `CURRENT_SESSION_VERSION`. Every browser whose stamp ≠ the
 * new constant runs a one-shot reset on next boot.
 *
 * Scope is intentionally narrow: only auth + onboarding keys are wiped.
 * UX-only state (theme, saved location, recent searches, follow lists)
 * survives a version bump.
 */

/**
 * Increment when a code change alters how auth-session or onboarding-gate
 * data is interpreted by the client. Examples:
 *   - JWT secret rotated on the backend
 *   - Cookie name or path changed
 *   - localStorage key renamed
 *   - Server flag (e.g. `onboardingCompletedAt`) added/removed/repurposed
 *
 * Do NOT bump for unrelated changes (UI tweaks, copy edits, dep bumps).
 * Every bump force-logs-out every active session — pay that cost only
 * when the alternative is silent breakage.
 */
export const CURRENT_SESSION_VERSION = 1 as const;

const VERSION_STORAGE_KEY = 'loci.session.version';

/**
 * Auth- and onboarding-scoped localStorage keys that lose meaning across a
 * version bump. Kept here (not imported from each module) so this file is
 * the single source of truth for what a session reset clears — adding a
 * new session-relevant key in another module without updating this list
 * is intentional friction that should be caught in code review.
 *
 * Keep this list short. Anything that is "stable user preference, not
 * session state" (theme, saved view, recent searches, follow cache) stays
 * out so users aren't punished for an unrelated deploy.
 */
const SESSION_SCOPED_KEYS: ReadonlyArray<string> = [
  'loci.session.present', // apps/web/lib/auth/sessionHint.ts → STORAGE_KEY
  // Legacy first-visit onboarding flag (route deleted under WEB-377; new
  // onboarding implementation will use its own key). Kept in the wipe list
  // so stale flags from old browsers are cleared on next session bump.
  'loci:onboardingCompleted',
];

export type SessionVersionResult =
  { status: 'aligned' } | { status: 'first-run' } | { status: 'wiped'; previousVersion: string };

/**
 * Reconcile the browser's stored session-version stamp against the
 * current build's `CURRENT_SESSION_VERSION`.
 *
 *   - `aligned`    — stamp matches; nothing happened.
 *   - `first-run`  — no stamp present; we wrote the current version
 *                    without wiping (a fresh browser doesn't need a reset).
 *   - `wiped`      — stamp drifted; we cleared every key in
 *                    `SESSION_SCOPED_KEYS` and wrote the new stamp. The
 *                    caller is responsible for cookie eviction and any
 *                    navigation (typically by invoking the existing
 *                    `logout()` flow from `useAuth()`).
 *
 * SSR-safe: returns `{ status: 'aligned' }` on the server and never
 * touches `window.localStorage` outside a `typeof window !== 'undefined'`
 * guard. Storage failures (private mode, quota, disabled cookies) are
 * swallowed — degrading to "treat as aligned" is preferable to crashing
 * the providers tree, and the existing stale-cookie recovery still fires
 * on the next `/auth/me` 401.
 */
export function reconcileSessionVersion(): SessionVersionResult {
  if (typeof window === 'undefined') return { status: 'aligned' };

  const current = String(CURRENT_SESSION_VERSION);
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(VERSION_STORAGE_KEY);
  } catch {
    return { status: 'aligned' };
  }

  if (stored === current) return { status: 'aligned' };

  // No prior version stamp — write the current one without wiping. A
  // fresh browser has no LOCI state to begin with; wiping would be a
  // no-op for empty keys but would still bounce the user through the
  // logout flow on every first visit.
  if (stored === null) {
    try {
      window.localStorage.setItem(VERSION_STORAGE_KEY, current);
    } catch {
      // ignore — best-effort
    }
    return { status: 'first-run' };
  }

  // Version drift — clear every session-scoped key, then write the new
  // stamp so the next boot sees `aligned`.
  for (const key of SESSION_SCOPED_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore — best-effort
    }
  }
  try {
    window.localStorage.setItem(VERSION_STORAGE_KEY, current);
  } catch {
    // ignore — even if we can't write the new stamp, the wipe still ran.
    // The next boot will detect drift again and retry; that's safer than
    // pretending the wipe succeeded.
  }

  return { status: 'wiped', previousVersion: stored };
}
